<template>
  <LoadingSpinner v-if="loading" />
  <Form v-else @submit="$emit('submit', zone)" :title="$t('meta.baseData')" :bodyClass="''">
    <FormInput :name="$t('zones.name')" v-model="zone.name" />

    <div class="form-wrap" v-if="costCenters.length > 0">
      <label>{{ $t('meta.costCenter') }}</label>
      <v-select
        class="form-select__select"
        v-model="zone.cost_center_uuid"
        :options="costCenterOptions"
        :disabled="costCenterSelectDisabled"
        :reduce="costCenter => costCenter.uuid"
        :searchable="true"
        :filterable="true"
      >
      </v-select>
    </div>

    <FormInput :name="$t('areas.description')" v-model="zone.description" labelOptional />
    <ZoneIdentifier
      v-if="!isNew"
      :identifier="zone.identifier ? zone.identifier : { name: '', identifier_type_name: '', value: '' }"
      :zoneUuid="zone.uuid"
      @zone-identifier-created="$emit('reload')"
    />
    <BaseMap
      :allowFourNewCoordinatesOnly="isNew"
      :editablePolygonFeature="geojsonFeature"
      :fitBounds="areaPolygon.properties.bbox"
      :staticPolygons="staticPolygonsForMap"
      :parentPolygon="areaPolygon"
      :enableDraw="isNew"
      @coordinates-updated="handleCoordinatesUpdate"
      @coordinates-reset="resetCoordinates"
      enableGeocoder
    />
    <Button type="submit">
      {{ $t('areas.update') }}
    </Button>
  </Form>
</template>

<script>
import Button from '@/components/Button/index.vue'
import FormInput from '@/components/Form/Input/index.vue'
import Form from '@/components/Form/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import BaseMap from '@/components/Map/base'
import ZoneIdentifier from '@/components/ZoneIdentifier'

import costCentersApi from '@/api/costCenters'
import zonesApi from '@/api/zones'
import { staticPolygonsFromObject } from '@/utils/geojson'
import mapUtils from '@/utils/mapbox'

export default {
  inject: ['companyUser'],
  data() {
    return {
      loading: true,
      zone: this.initialZone || {
        company_uuid: this.companyUser.company_uuid,
      },
      otherZones: [],
      costCenters: [],
    }
  },
  props: {
    initialZone: { type: Object },
    area: { type: Object, required: true },
    site: { type: Object, required: true },
  },
  components: {
    Form,
    FormInput,
    LoadingSpinner,
    ZoneIdentifier,
    BaseMap,
    Button,
  },
  methods: {
    handleCoordinatesUpdate(newCoordinates) {
      const center = mapUtils.calculateCenterOfCoordinates(mapUtils.coordinatePairArrayToLngLatObject(newCoordinates))
      this.zone.mb_feature_polygon = JSON.stringify({
        type: 'FeatureCollection',
        features: [
          {
            id: this.zone.uuid,
            type: 'Feature',
            propertiers: {},
            geometry: {
              coordinates: [newCoordinates],
              type: 'Polygon',
            },
          },
        ],
      })

      this.zone.lat = center.lat
      this.zone.lng = center.lng
    },
    resetCoordinates() {
      this.zone.lat = null
      this.zone.lng = null
    },
  },
  computed: {
    isNew() {
      return !this.zone.uuid
    },
    staticPolygonsForMap() {
      return [
        staticPolygonsFromObject('site', this.site),
        this.areaPolygon,
        ...this.otherZones.map(zone => staticPolygonsFromObject('zone', zone)),
      ]
    },
    areaPolygon() {
      return staticPolygonsFromObject('area', this.area)
    },
    geojsonFeature() {
      if (this.isNew) return null
      return JSON.parse(this.zone.mb_feature_polygon).features[0]
    },
    costCenterOptions() {
      return this.costCenters.map(function (costCenter) {
        return { label: costCenter.name, uuid: costCenter.uuid }
      })
    },
    costCenterSelectDisabled() {
      if (this.companyUser.user.admin) return false
      if (this.companyUser.roles.includes('company_admin')) return false

      return true
    },
  },
  async mounted() {
    this.otherZones = (await zonesApi.getAllInSite(this.$route.params.site_uuid)).filter(
      zone => zone.uuid !== this.$route.params.zone_uuid,
    )

    this.costCenters = await costCentersApi.getPaginated({ company_uuid: this.companyUser.company_uuid, active: true })

    this.loading = false
  },
}
</script>
